import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Products } from './pages/Products';
import { Entries } from './pages/Entries';
import { Exits } from './pages/Exits';
import { DeliveryNotes } from './pages/DeliveryNotes';
import { Suppliers } from './pages/Suppliers';
import { Eventos } from './pages/Eventos';
import { EventDetail } from './pages/EventDetail';
import { Companies } from './pages/Companies';
import { CompanySettings } from './pages/CompanySettings';
import { UserManagement } from './pages/UserManagement';
import { Operators } from './pages/Operators';
import { Scanner } from './pages/Scanner';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Role constants to avoid inline array recreation on each render
const ROLES_ADMIN = ['super_admin', 'admin', 'user'];
const ROLES_WITH_OPERATOR = ['super_admin', 'admin', 'user', 'operator'];
const ROLES_SUPER_ADMIN = ['super_admin'];
const ROLES_ADMIN_ONLY = ['super_admin', 'admin'];

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Si es operador y intenta acceder a algo no permitido, redirigir al scanner
    if (user.role === 'operator') {
      return <Navigate to="/scanner" />;
    }
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={
        user ? (
          user.role === 'operator' ? <Navigate to="/scanner" /> : <Navigate to="/dashboard" />
        ) : <Login />
      } />
      
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/products" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <Layout><Products /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/entries" element={
        <ProtectedRoute allowedRoles={ROLES_WITH_OPERATOR}>
          <Layout><Entries /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/exits" element={
        <ProtectedRoute allowedRoles={ROLES_WITH_OPERATOR}>
          <Layout><Exits /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/delivery-notes" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <Layout><DeliveryNotes /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/suppliers" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN}>
          <Layout><Suppliers /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/customers" element={
        <ProtectedRoute allowedRoles={ROLES_WITH_OPERATOR}>
          <Layout><Eventos /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/events" element={
        <ProtectedRoute allowedRoles={ROLES_WITH_OPERATOR}>
          <Layout><Eventos /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/events/:eventId" element={
        <ProtectedRoute allowedRoles={ROLES_WITH_OPERATOR}>
          <Layout><EventDetail /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/companies" element={
        <ProtectedRoute allowedRoles={ROLES_SUPER_ADMIN}>
          <Layout><Companies /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/company-settings" element={
        <ProtectedRoute>
          <Layout><CompanySettings /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN_ONLY}>
          <Layout><UserManagement /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/operators" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN_ONLY}>
          <Layout><Operators /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/scanner" element={
        <ProtectedRoute allowedRoles={ROLES_WITH_OPERATOR}>
          <Layout><Scanner /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/" element={
        <Navigate to={user ? (user.role === 'operator' ? "/scanner" : "/dashboard") : "/login"} />
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WebSocketProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </WebSocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;